import { type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { hasErrorResponse } from '@/apis/apiUtils';
import { type SpenditError, type SpenditErrorResponse } from '@/apis/shared';

export const encodeCertificateFile = async (file: File) => {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
  return btoa(binary);
};

const getRequestBody = (data: unknown): Record<string, unknown> | undefined => {
  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data);
      return getRequestBody(parsed);
    } catch {
      return undefined;
    }
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return Object.fromEntries(Object.entries(data));
  }
  return undefined;
};

const isCertificateApiRequest = (config?: AxiosRequestConfig) => {
  const path = config?.url?.split('?')[0];
  if (
    config?.method?.toLowerCase() !== 'post' ||
    !path ||
    !/^\/v(?:4\/user_card\/login|4\/user_cards\/\d+\/login|5\/user_cards)$/.test(path)
  )
    return false;
  return getRequestBody(config.data)?.authentication_method === 'certificate';
};

type CertificateError = SpenditError<number, string, { authentication_state?: string }>;

const getCertificateError = (error: unknown) =>
  hasErrorResponse<SpenditErrorResponse<CertificateError> | undefined>(error) ? error.response.data?.error : undefined;

export const isCertificateAccountTokenError = (error: unknown) => getCertificateError(error)?.field === 'account_token';

export const requiresCertificateReconnection = (error: unknown) =>
  getCertificateError(error)?.extra_data?.authentication_state === 'reconnection_required';

export const isCertificateSchemaUnavailableResponse = (response?: AxiosResponse<SpenditErrorResponse | undefined>) =>
  response?.status === 503 &&
  isCertificateApiRequest(response.config) &&
  response.data?.error?.code === 50000 &&
  response.data.error.field === 'authentication_method';
